import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import {
  PlayerRepository,
  TeamRepository,
  UserRepository,
} from '../../../state/reduxRepositories';
import { Team } from '../../../state/reduxRepositories/teams/team.interfaces';
import { useAppSelector } from '../../../state/hooks';
import { Player } from '../../../state/reduxRepositories/players/player.interfaces';
import { GlobalQueryService } from '../../../state/queryParams/global.query.service';
import { ListTeamsQueryParamService } from './listTeams.queryParam.service';
import { AuthService } from '../../../state/helperServices/auth/auth.service';
import {
  Role,
  User,
} from '../../../state/reduxRepositories/users/users.interfaces';
import { LoggedInUser } from '../../../state/helperServices/auth/auth.interfaces';

@injectable()
export class ListTeamDomainService {
  teamWithPlayersSelector: any;
  teamWithPlayerSelector: any;
  teamsWithManagerSelector: any;
  teamsHomePageWithManagerSelector: any;

  constructor(
    private teamRepository: TeamRepository,
    private playerRepository: PlayerRepository,
    private globalQueryService: GlobalQueryService,
    private teamsPageQueryService: ListTeamsQueryParamService,
    private userRepository: UserRepository,
    private authService: AuthService,
  ) {
    this.createTeamWithPlayersSelector();
    this.createTeamWithPlayerSelector();
    this.createTeamsWithManagerSelector();
    this.createTeamsHomePageWithManagerSelector();
  }

  dispatchRemoveTeam = (id: string) => {
    return this.teamRepository.dispatchDelete(id);
  };

  usePlayerResourceSelector = () => {
    return useAppSelector(this.playerRepository.selectors.resourcesSelector);
  };

  useUserResourceSelector = () => {
    return useAppSelector(this.userRepository.selectors.resourcesSelector);
  };

  useTeamQueryServiceSelector = () => {
    return this.teamsPageQueryService.useQuerySelector();
  };

  useUserSelector = () => {
    return useAppSelector(this.authService.loggedInUserSelector);
  };

  useTeamWithPlayersSelector = (): Team[] => {
    return useAppSelector(this.teamWithPlayersSelector);
  };

  useTeamWithPlayerSelector = (): Team => {
    return useAppSelector(this.teamWithPlayerSelector);
  };

  useTeamsWithManagerSelector = (): Team[] => {
    return useAppSelector(this.teamsWithManagerSelector);
  };

  useTeamsHomePageWithManagerSelector = (): Team[] => {
    return useAppSelector(this.teamsHomePageWithManagerSelector);
  };

  private createTeamsHomePageWithManagerSelector = () => {
    this.teamsHomePageWithManagerSelector = createSelector(
      this.teamRepository.selectors.resourcesSelector,
      this.playerRepository.selectors.resourcesSelector,
      this.authService.loggedInUserSelector,
      (teams: Team[] | undefined, players: Player[], auth: LoggedInUser) => {
        const filteredTeams = teams?.filter((team: Team) =>
          team.teamManagerUserIds.includes(auth?.id),
        );
        const teamsWithPlayers = filteredTeams?.map((team: Team) => {
          return {
            ...team,
            players: players.filter((player) =>
              team.playerIds.includes(player.id),
            ),
          };
        });
        const teamsSorted = teamsWithPlayers?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        return teamsSorted;
      },
    );
  };

  private createTeamsWithManagerSelector = () => {
    this.teamsWithManagerSelector = createSelector(
      this.teamRepository.selectors.resourcesSelector,
      this.playerRepository.selectors.resourcesSelector,
      this.authService.loggedInUserSelector,
      (teams: Team[] | undefined, players: Player[], auth: LoggedInUser) => {
        const filteredTeams = teams?.filter((team: Team) =>
          team.teamManagerUserIds.includes(auth?.id),
        );
        const result = auth?.role.includes(Role.ADMIN) ? teams : filteredTeams;
        const teamsWithPlayers = result?.map((team: Team) => {
          return {
            ...team,
            players: players.filter((player) =>
              team.playerIds.includes(player.id),
            ),
          };
        });

        const teamsSorted = teamsWithPlayers?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return teamsSorted;
      },
    );
  };

  private createTeamWithPlayersSelector = () => {
    this.teamWithPlayersSelector = createSelector(
      this.teamRepository.selectors.resourcesSelector,
      this.playerRepository.selectors.resourcesSelector,
      this.userRepository.selectors.resourcesSelector,
      (teams: Team[] | undefined, players: Player[], users: User[]) => {
        return teams?.map((team: Team) => {
          return {
            ...team,
            players: players.filter((player) =>
              team.playerIds.includes(player.id),
            ),
            teamManager: users.filter((user) =>
              team?.teamManagerUserIds.includes(user.id),
            ),
          };
        });
      },
    );
  };

  private createTeamWithPlayerSelector = () => {
    this.teamWithPlayerSelector = createSelector(
      this.globalQueryService.globalQuerySelector,
      this.teamRepository.selectors.resourcesSelector,
      this.playerRepository.selectors.resourcesSelector,
      this.userRepository.selectors.resourcesSelector,
      (
        queryParams: any,
        teams: Team[] | undefined,
        players: Player[],
        users: User[],
      ) => {
        const findTeam = teams?.find(
          (team: Team) => team.id === queryParams?.selectedTeamId,
        );
        return {
          ...findTeam,
          players: players.filter((player) =>
            findTeam?.playerIds.includes(player.id),
          ),
          teamManager: users.filter((user) =>
            findTeam?.teamManagerUserIds.includes(user.id),
          ),
        };
      },
    );
  };
}
